import { useState } from "react";
import { copyText } from "@/lib/clipboard";
import { Logo } from "./Nav";

export function Vision() {
  return (
    <section className="vision" id="vision">
      <div className="container">
        <div className="features-header">
          <span className="section-label">愿景</span>
          <h2 className="section-title">
            把 AI 能力，<br />
            <span className="gradient-text">交到更多人手里</span>
          </h2>
          <p className="section-desc">
            前沿 AI 工具不该只属于会折腾环境、懂依赖、懂命令行的人。
            <br />
            Agentfly 把模型、运行时、配置、网络这些门槛收进工厂，
            交付成一个开箱就能干活的智能体。这就是我们的科技平权。
          </p>
        </div>

        <div className="pipeline">
          <PipelineStep n="01" title="挑好工具">
            不用到处比较项目、版本和平台兼容性，我们先把适合日常使用的智能体挑出来。
          </PipelineStep>
          <span className="pipeline-arrow" aria-hidden="true">→</span>
          <PipelineStep n="02" title="配好环境">
            把运行时、依赖、路径和默认配置一起处理好，尽量让新手不用读长篇安装文档。
          </PipelineStep>
          <span className="pipeline-arrow" aria-hidden="true">→</span>
          <PipelineStep n="03" title="一键交付">
            一行命令送到你的电脑，自动探测平台、自动下载、自动配置，少做选择题。
          </PipelineStep>
          <span className="pipeline-arrow" aria-hidden="true">→</span>
          <PipelineStep n="04" title="持续更新">
            上游工具迭代后重新打包，用户继续跑熟悉的命令，不必重新学习安装流程。
          </PipelineStep>
        </div>
      </div>
    </section>
  );
}

function PipelineStep({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <div className="pipeline-step">
      <div className="pipeline-step-num">{n}</div>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

const FEATURES: Array<{ icon: string; title: string; body: React.ReactNode }> = [
  {
    icon: "⚡",
    title: "一行命令搞定",
    body: (
      <>
        Linux/macOS 一条 <code>curl … | bash</code>，
        Windows 一条 <code>irm … | iex</code>。
        复制、粘贴、回车，后面的平台识别和安装流程自动完成。
      </>
    ),
  },
  {
    icon: "🧭",
    title: "小白友好",
    body: "不要求先学会 Python、Node、WSL、PATH、代理和配置文件。能照着复制命令，就能把智能体装起来。",
  },
  {
    icon: "📦",
    title: "与官方安装一致",
    body: "安装路径、二进制位置、运行时版本、配置文件 —— 全部对齐上游官方。后续升级、迁移、卸载没有学习成本。",
  },
  {
    icon: "🪂",
    title: "多种智能体，一处入口",
    body: "Hermes、openclaw、Codex、Claude Code、WSL —— 通通用同一个入口安装。新智能体出厂，不用换工具。",
  },
  {
    icon: "🚀",
    title: "开箱即用",
    body: "网络优化、镜像源、运行时、依赖拉取 —— 常见的本地化适配已经做完，不再为一个失败的依赖耗一下午。",
  },
  {
    icon: "🤝",
    title: "科技平权",
    body: "让 AI 编程工具不再只服务少数会配置环境的人。学生、运营、设计、产品和开发者都能从同一个入口开始。",
  },
];

export function Features() {
  return (
    <section className="features">
      <div className="container">
        <div className="features-header">
          <span className="section-label">为什么选 Agentfly</span>
          <h2 className="section-title">让 AI 智能体，<br />回到「复制回车就能用」</h2>
          <p className="section-desc">
            自己拼装智能体环境，常常卡在依赖、版本、网络和配置上。
            Agentfly 把这些杂事一次性解决，让第一次接触命令行的人也能顺利开始。
          </p>
        </div>

        <div className="features-grid">
          {FEATURES.map((f) => (
            <div key={f.title} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  return (
    <section className="how-it-works" id="how">
      <div className="container">
        <div className="how-header">
          <span className="section-label">工作方式</span>
          <h2 className="section-title">从授权到可用，<br />只走这 4 步</h2>
          <p className="section-desc">没有复杂表单，没有长篇配置。照着做，一杯水的时间，智能体就位。</p>
        </div>

        <div className="steps">
          <Step n={1} title="获取授权" code="license">
            从团队同事或自助通道拿到一个授权码，不需要先理解底层配置。
          </Step>
          <Step n={2} title="一键运行" code="curl · irm">
            复制对应平台的命令，粘到终端按回车。剩下的事情交给脚本。
          </Step>
          <Step n={3} title="自动安装" code="auto install">
            探测平台、下载、校验、解压、配置一气呵成，尽量不打断你提问。
          </Step>
          <Step n={4} title="立即可用" code="ready to go">
            启动器写入 PATH，直接运行 <code>claude</code>、<code>codex</code>、<code>hermes</code>。
          </Step>
        </div>
      </div>
    </section>
  );
}

function Step({ n, title, code, children }: { n: number; title: string; code: string; children: React.ReactNode }) {
  return (
    <div className="step">
      <div className="step-number">{n}</div>
      <h3>{title}</h3>
      <p>{children}</p>
      <span className="step-code">{code}</span>
    </div>
  );
}

const FAQ = [
  {
    q: "完全不会配环境也能用吗？",
    a: "可以。Agentfly 的目标就是把安装门槛降到复制命令和按回车。你不需要先理解运行时、依赖目录或模型配置。",
  },
  {
    q: "Agentfly 跟自己手装有什么区别？",
    a: "没有区别。Agentfly 安装的就是官方版本，路径、运行时、配置文件全部对齐官方文档。后续升级、迁移、卸载完全可以按官方流程走。",
  },
  {
    q: "会不会拿到我的密钥或对话内容？",
    a: "不会。Agentfly 是分发平台，只负责把官方智能体交付到你的机器上。你的 API Key、对话内容、项目代码都在本地，与我们无关。",
  },
  {
    q: "授权码能给同事用吗？",
    a: "授权码是按使用次数发放的，请按团队规模申请对应配额，不要混用。团队批量授权请直接邮件联系我们。",
  },
  {
    q: "支持哪些操作系统？",
    a: "Linux x86_64 / arm64、macOS Apple Silicon、Windows x86_64。Intel Mac 暂未官方支持。",
  },
  {
    q: "未来会有更多智能体出厂吗？",
    a: "会。Agentfly 的目标是成为 AI 时代的智能体工厂，生产线会持续接入主流 AI 编程工具与开发环境。欢迎提名你想用的智能体。",
  },
  {
    q: "装失败了怎么办？",
    a: "把命令的完整输出发给我们，邮件 hello@agentfly.net，我们会尽快回复。",
  },
];

export function Faq() {
  return (
    <section className="faq" id="faq">
      <div className="container">
        <div className="features-header">
          <span className="section-label">常见问题</span>
          <h2 className="section-title">你可能会问</h2>
        </div>
        <div className="faq-list">
          {FAQ.map((item) => (
            <details key={item.q} className="faq-item">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Cta() {
  return (
    <section className="cta">
      <div className="container">
        <div className="cta-inner">
          <div className="cta-glow" aria-hidden="true" />
          <h2>智能体<span className="gradient-text">出厂</span>，<br />从这里开始</h2>
          <p>
            把繁琐的安装流程交给 Agentfly，
            让更多人平等用上前沿 AI 工具。
          </p>
          <div className="cta-actions">
            <a href="mailto:hello@agentfly.net" className="btn-primary">
              申请授权 →
            </a>
            <CtaInstallButton />
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaInstallButton() {
  const cmd = "curl -fsSL https://agentfly.net/install.sh | bash -s -- --token=<TOKEN>";
  return <CtaCopy cmd={cmd} />;
}

function CtaCopy({ cmd }: { cmd: string }) {
  // 旧 CTA 按钮内含 cmd + label 两个 span，复制反馈只切 label。
  // 内联实现避免 CopyButton 默认整 textContent 替换。
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    await copyText(cmd);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      type="button"
      className={"cta-install" + (copied ? " copied" : "")}
      onClick={handle}
      aria-label="复制安装命令"
    >
      <span className="cta-install-cmd">$ curl -fsSL agentfly.net/install.sh | bash -s -- --token=…</span>
      <span className="cta-install-copy">{copied ? "已复制" : "复制"}</span>
    </button>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="container footer-inner">
        <div className="footer-left">
          <Logo />
          <span>Agentfly · 呀哈智体 · AI 时代的智能体工厂</span>
        </div>
        <ul className="footer-links">
          <li><a href="#market">应用市场</a></li>
          <li><a href="#vision">愿景</a></li>
          <li><a href="#how">工作方式</a></li>
          <li><a href="#faq">常见问题</a></li>
          <li><a href="mailto:hello@agentfly.net">联系我们</a></li>
        </ul>
      </div>
    </footer>
  );
}
